import { ChangeDetectionStrategy, Component, OnInit, computed, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { DashboardService, SubscriptionPaymentApi } from '../dashboard.service';
import { LanguageService } from '../language.service';
import { LoginService } from '../login.service';
import { MONTH_ABBR } from '../interfaces/dashboard.component';

// A payment enriched with the fields the list renders/filters on.
interface ViewPayment extends SubscriptionPaymentApi {
  name: string; // document type name (or a fallback)
  dateLabel: string; // "Jun 12, 2026"
  year: string; // "2026" — drives the year filter
  signed: number; // amount, negative for credit notes
}

@Component({
  selector: 'app-dashboard-artisan-payments',
  standalone: false,
  templateUrl: './dashboard-artisan-payments.html',
  styleUrl: './dashboard-artisan-payments.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardArtisanPaymentsComponent implements OnInit {
  readonly loading = signal(true);
  private readonly rawPayments = signal<SubscriptionPaymentApi[]>([]);

  readonly search = signal('');
  readonly yearFilter = signal('');
  readonly typeFilter = signal('');

  // The PDF currently open in the shared embed modal (null = closed).
  readonly modalUrl = signal<SafeResourceUrl | null>(null);

  readonly dir = signal<'rtl' | 'ltr'>('ltr');
  private readonly lang = signal('');

  readonly userName: string;
  readonly userNameLast: string;
  readonly isAdmin: boolean;
  private readonly hour = signal(new Date().getHours());
  readonly greeting = computed(() => {
    this.lang();
    const h = this.hour();
    if (h < 12) return this.translate.instant('GUI.dashboard.labels.good morning');
    if (h < 18) return this.translate.instant('GUI.dashboard.labels.good afternoon');
    return this.translate.instant('GUI.dashboard.labels.good evening');
  });
  readonly today = computed(() => {
    this.lang();
    const locale = this.translate.currentLang || 'en-US';
    return new Date().toLocaleDateString(locale, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  });

  // Full set enriched for display/filtering, newest first (server already sorts).
  private readonly payments = computed<ViewPayment[]>(() =>
    this.rawPayments().map((p) => {
      const dt = p.date ? new Date(p.date) : null;
      const amount = Number(p.amount ?? 0);
      return {
        ...p,
        name: p.typeName ?? '',
        dateLabel: dt ? `${MONTH_ABBR[dt.getMonth()]} ${dt.getDate()}, ${dt.getFullYear()}` : '',
        year: dt ? String(dt.getFullYear()) : '',
        signed: p.credit ? -Math.abs(amount) : amount,
      };
    })
  );

  // Distinct years (desc) and document types present, for the filter dropdowns.
  readonly years = computed<string[]>(() =>
    Array.from(
      new Set(
        this.payments()
          .map((p) => p.year)
          .filter(Boolean)
      )
    ).sort((a, b) => b.localeCompare(a))
  );
  readonly types = computed<string[]>(() =>
    Array.from(
      new Set(
        this.payments()
          .map((p) => p.name)
          .filter(Boolean)
      )
    ).sort()
  );

  readonly filtered = computed<ViewPayment[]>(() => {
    const q = this.search().trim().toLowerCase();
    const year = this.yearFilter();
    const type = this.typeFilter();
    return this.payments().filter((p) => {
      if (year && p.year !== year) return false;
      if (type && p.name !== type) return false;
      if (!q) return true;
      return (
        p.name.toLowerCase().includes(q) ||
        (p.documentNumber ?? '').toLowerCase().includes(q) ||
        String(Math.abs(p.signed)).includes(q)
      );
    });
  });

  // Summary strip: this-month total, document count, last payment date.
  readonly summary = computed(() => {
    const now = new Date();
    const list = this.payments();
    let thisMonth = 0;
    for (const p of list) {
      if (!p.date) continue;
      const dt = new Date(p.date);
      if (dt.getFullYear() === now.getFullYear() && dt.getMonth() === now.getMonth()) {
        thisMonth += p.signed;
      }
    }
    return {
      thisMonth,
      count: list.length,
      lastLabel: list[0]?.dateLabel ?? null, // null → "no data available" in the view
    };
  });

  constructor(
    private dashboardService: DashboardService,
    private sanitizer: DomSanitizer,
    private router: Router,
    private translate: TranslateService,
    private languageService: LanguageService,
    private loginService: LoginService
  ) {
    this.dir.set(this.languageService.pageDirection());
    this.translate.onLangChange.pipe(takeUntilDestroyed()).subscribe((e) => {
      this.dir.set(this.languageService.pageDirection());
      this.lang.set(e.lang);
    });

    const parts = (loginService.username ?? '')
      .trim()
      .split(/\s+/)
      .filter((p) => p && p.toLowerCase() !== 'null');
    this.userNameLast = parts.pop() ?? '';
    this.userName = parts.join(' ');
    this.isAdmin = loginService.isAdmin;
  }

  ngOnInit(): void {
    this.dashboardService.getSubscriptionHistory().subscribe({
      next: (data) => {
        this.rawPayments.set(data?.payments ?? []);
        this.loading.set(false);
      },
      error: () => this.loading.set(false),
    });
  }

  // Format a signed amount as "₪1,200" / "−₪90".
  money(amount: number): string {
    return (amount < 0 ? '−₪' : '₪') + Math.abs(amount).toLocaleString();
  }

  openPreview(payment: ViewPayment): void {
    if (!payment.id) return;
    const url = `/api/dashboard/subscription/${payment.id}/pdf`;
    this.modalUrl.set(this.sanitizer.bypassSecurityTrustResourceUrl(url));
  }

  closeModal(): void {
    this.modalUrl.set(null);
  }

  downloadHref(payment: ViewPayment): string {
    return `/api/dashboard/subscription/${payment.id}/pdf/download`;
  }

  goDashboard(): void {
    this.router.navigate(['/dashboard']);
  }
}
